import { useLocation } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';

/** Na tela de planos a faixa repetiria o título da página e o botão levaria para ela mesma. */
const HIDDEN_ON = ['/subscription/plans'];

/** Acima disto a faixa não mostra a contagem (ex.: "3636 dias" numa conta importada). */
const MAX_COUNTED_DAYS = 30;

/**
 * Dias de calendário entre hoje e o fim do trial, no relógio de quem usa.
 * trial_ends_at vem do backend em UTC ('Y-m-d H:i:s'). Sem data válida, devolve null.
 */
const getCalendarDaysUntil = (trialEndsAt) => {
  if (!trialEndsAt) return null;
  const end = new Date(`${String(trialEndsAt).replace(' ', 'T')}Z`);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfEndDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((startOfEndDay - startOfToday) / 86400000);
};

/**
 * Estado da faixa do teste (TrialBanner e a faixa única do celular, TrialDemoCompactBanner).
 * visible: trial interno ativo, sem assinatura Stripe e fora da tela de planos.
 * Os textos seguem os dias de calendário, como o e-mail de aviso.
 */
export default function useTrialBanner() {
  const { pathname } = useLocation();
  const { isInActiveTrial, getTrialDaysRemaining, subscription, trialInfo } = useSubscription();

  // Só mostrar se estiver em trial (não em assinatura Stripe paga)
  const hasStripeSubscription = subscription && (
    subscription.has_active_plan ||
    subscription.subscription_status === 'trialing' ||
    subscription.stripe_subscription_status === 'trialing'
  );
  const visible = !hasStripeSubscription && isInActiveTrial() && !HIDDEN_ON.includes(pathname);

  const calendarDays = getCalendarDaysUntil(trialInfo?.trial_ends_at);
  const daysRemaining = calendarDays ?? getTrialDaysRemaining();
  // Conta com fim do teste muito distante (importada, combinada): sem contagem de dias
  const isLongTrial = daysRemaining > MAX_COUNTED_DAYS;

  const getMessage = () => {
    // Dias de calendário, como o e-mail de aviso: "amanhã" com 1 dia, "último dia" no dia do fim
    if (calendarDays === 0) return 'Último dia do seu teste gratuito';
    if (calendarDays === 1) return 'Seu teste gratuito termina amanhã';
    if (daysRemaining <= 0) return 'Seu teste encerrou hoje';
    if (daysRemaining === 1) return 'Último dia do seu teste gratuito';
    if (daysRemaining <= 3) return `Apenas ${daysRemaining} dias restantes no seu teste gratuito`;
    if (daysRemaining <= 7) return `Seu teste gratuito termina em ${daysRemaining} dias`;
    if (isLongTrial) return 'Você está no período gratuito para explorar tudo';
    return `Você está no período gratuito — ${daysRemaining} dias para explorar tudo`;
  };

  // Versão curta para o celular: cabe em uma linha ao lado do botão
  const getShortMessage = () => {
    if (calendarDays === 0) return 'Último dia do teste gratuito';
    if (calendarDays === 1) return 'Teste gratuito termina amanhã';
    if (daysRemaining <= 0) return 'Seu teste encerrou hoje';
    if (daysRemaining === 1) return 'Último dia do teste gratuito';
    if (daysRemaining <= 7) return `Teste gratuito: ${daysRemaining} dias`;
    if (isLongTrial) return 'Período gratuito';
    return `Período gratuito: ${daysRemaining} dias`;
  };

  // Mínima, para dividir a linha com outro aviso ("30 dias · com exemplos")
  const getTinyMessage = () => {
    if (calendarDays === 0) return 'Último dia';
    if (calendarDays === 1) return 'Termina amanhã';
    if (daysRemaining <= 0) return 'Teste encerrado';
    if (daysRemaining === 1) return 'Último dia';
    if (isLongTrial) return 'Período gratuito';
    return `${daysRemaining} dias`;
  };

  const getUrgency = () => {
    if (daysRemaining <= 1) return 'urgent';
    if (daysRemaining <= 3) return 'warning';
    return 'info';
  };

  return {
    visible,
    message: getMessage(),
    shortMessage: getShortMessage(),
    tinyMessage: getTinyMessage(),
    urgency: getUrgency(),
  };
}
